<template>
    <div class="bg-indigo-100 rounded items-center">
        <div v-if="debugStore.debugMode && value.id" class="w-full font-mono">
            [TableCell:{{ value.id }}]
        </div>

        <Select
            class="w-full"
            label="* Cell Type"
            name="type_id"
            v-model.number="value.type_id"
            placeholder="Select Cell Type"
            @input="(val) => $emit('cell-type-changed', val)"
            unsettable
            :full-width="true"
            :disabled="disabled"
            :options="[
                { value: TABLE_CELL_TYPES.TEXT, name: 'Text' },
                { value: TABLE_CELL_TYPES.TEXT_WITH_ICON, name: 'Text with Icon' },
                { value: TABLE_CELL_TYPES.LINK, name: 'Link' },
                { value: TABLE_CELL_TYPES.LOGO, name: 'Logo' },
                { value: TABLE_CELL_TYPES.LOGO_WITH_LINK, name: 'Logo with Link' },
                { value: TABLE_CELL_TYPES.REFERENCE_LABEL, name: 'Reference Label' },
                { value: TABLE_CELL_TYPES.REFERENCE_VALUE, name: 'Reference Value' },
                { value: TABLE_CELL_TYPES.REFERENCE_VALUE_WITH_LINK, name: 'Reference Value with Link' },
            ]"
            :error="error?.type_id ? error?.type_id[0] : null"
        />

        <div v-if="value.type_id === TABLE_CELL_TYPES.REFERENCE_LABEL">
            <Select
                v-model="value.reference_label"
                :options="listAttributes"
                name="reference_label"
                :full-width="true"
                :disabled="disabled"
                placeholder="Select a label"
                unsettable
                @input="(val) => $emit('reference-label-changed', val)"
                :error="error?.reference_label ? error?.reference_label[0] : null"
            />

            <input-checkbox
                name="with_icon"
                label="With Icon"
                class="mt-3"
                v-model="value.with_icon"
                :disabled="disabled"
                :error="error?.with_icon ? error?.with_icon[0] : null"
            />
        </div>

        <Select
            v-if="[TABLE_CELL_TYPES.REFERENCE_VALUE, TABLE_CELL_TYPES.REFERENCE_VALUE_WITH_LINK].includes(value.type_id)"
            v-model="value.reference_value"
            :options="listAttributes"
            :disabled="disabled"
            name="reference_value"
            :full-width="true"
            placeholder="Select a value"
            unsettable
            :error="error?.reference_value ? error?.reference_value[0] : null"
        />

        <div class="w-full text-red-700 mt-3"
             v-if="[
                 TABLE_CELL_TYPES.REFERENCE_LABEL,
                 TABLE_CELL_TYPES.REFERENCE_VALUE,
                 TABLE_CELL_TYPES.REFERENCE_VALUE_WITH_LINK,
             ].includes(value.type_id) && !documentType"
        >
            Reference Data is required for Reference cells.
        </div>

        <div v-if="[TABLE_CELL_TYPES.TEXT, TABLE_CELL_TYPES.TEXT_WITH_ICON].includes(value.type_id)">
            <input-textarea
                v-model="value.text"
                :language="language"
                :maxlength="255"
                :disabled="disabled"
                class="w-full"
                :full-width="true"
                name="text"
                :error="error?.text ? error?.text[0] : null"
            />

            <input-checkbox
                name="Bold"
                label="Bold"
                class="mt-3"
                v-model="value.bold"
                :disabled="disabled"
                :error="error?.bold ? error?.bold[0] : null"
            />
        </div>

        <media-reference
            v-if="value.type_id === TABLE_CELL_TYPES.TEXT_WITH_ICON"
            v-model="value.icon"
            :language="language"
            :media-types="['Icon']"
            class="col-span-2 mt-4"
            :full-width="true"
            name="icon"
            :disabled="disabled"
            :error="error?.icon_id ? error?.icon_id[0] : null"
        />

        <rocket-link
            v-if="[
                TABLE_CELL_TYPES.LINK,
                TABLE_CELL_TYPES.LOGO_WITH_LINK,
                TABLE_CELL_TYPES.REFERENCE_VALUE_WITH_LINK,
            ].includes(value.type_id)"
            v-model="value.link"
            :hide-link-text="[
                TABLE_CELL_TYPES.LOGO_WITH_LINK,
                TABLE_CELL_TYPES.REFERENCE_VALUE_WITH_LINK,
            ].includes(value.type_id)"
            :language="language"
            :resettable="true"
            :disabled="disabled"
            class="col-span-2 mt-4"
        />
        <div class="w-full text-red-700" v-if="error?.link">
            {{ error?.link?.join(', ') }}
        </div>

        <input-checkbox
            v-if="value.type_id === TABLE_CELL_TYPES.LINK"
            name="cta_style"
            label="CTA Style"
            class="mt-3"
            v-model="value.cta_style"
            :disabled="disabled"
            :error="error?.cta_style ? error?.cta_style[0] : null"
        />

        <media-reference
            v-if="[
                TABLE_CELL_TYPES.LOGO,
                TABLE_CELL_TYPES.LOGO_WITH_LINK,
            ].includes(value.type_id)"
            v-model="value.logo"
            :language="language"
            :media-types="['OperatorLogo', 'PaymentLogo', 'SoftwareLogo', 'LicenseLogo', 'Logo']"
            class="col-span-2 mt-4"
            :full-width="true"
            :disabled="disabled"
            name="logo"
            :error="error?.logo_id ? error?.logo_id[0] : null"
        />

        <div class="w-full text-red-700" v-if="error?.$">
            {{ error?.$?.join(', ') }}
        </div>
    </div>
</template>

<script>
import CasinoAttributes from '../../tableSchema/casino';
import GameAttributes from '../../tableSchema/game';
import LicenseAttributes from '../../tableSchema/license';
import PaymentMethodAttributes from '../../tableSchema/paymentMethod';
import SlotAttributes from '../../tableSchema/slot';
import SoftwareAttributes from '../../tableSchema/software';
import siteLanguage from '../../config/siteLanguage'
import RocketLink from "../Link/RocketLink.vue";
import InputTextarea from "../form/InputTextarea.vue";
import {useDebugStore} from "../../stores/debug";
import tableConstants from "../../mixins/tableConstants";

export default {
    name: "TableCell",
    mixins: [tableConstants],
    components: {InputTextarea, RocketLink},
    props: {
        value: {
            required: false,
            type: Object,
        },
        language: {
            type: Object,
            default: () => {
                return {
                    iso: siteLanguage.iso.ENGLISH,
                    id: siteLanguage.language.ENGLISH,
                };
            },
        },
        typeId: {
            type: Number,
            required: true,
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        documentType: {
            type: String,
            required: false,
        },
        error: {
            type: Object,
            required: false,
        },
    },
    data() {
        return {
            debugStore: useDebugStore(),
        };
    },
    computed: {
        attributes() {
            switch (this.documentType) {
                // the filter below can be removed once we can remove manual entry text from casino.json - when the old table builder is not used anymore
                case 'App\\Models\\Casino': return CasinoAttributes.filter(attr => attr.key !== 'manualEntryText()');
                case 'App\\Models\\Game': return GameAttributes;
                case 'App\\Models\\License': return LicenseAttributes;
                case 'App\\Models\\PaymentMethod': return PaymentMethodAttributes;
                case 'App\\Models\\Slot': return SlotAttributes;
                case 'App\\Models\\Software': return SoftwareAttributes;
                default: return [];
            }
        },
        listAttributes() {
            return this.attributes.map((attribute) => {
                return {
                    value: attribute.key,
                    name: attribute.name
                }
            });
        },
    },
    watch: {
        "value.icon": function (newVal) {
            this.value.icon_id = newVal?.id ?? null;
        },
        "value.logo": function (newVal) {
            this.value.logo_id = newVal?.id ?? null;
        },
        "value.type_id": function (newVal) {
            // TODO do we need to add confirmation here before resetting values?

            if (this.value.type_id !== this.TABLE_CELL_TYPES.REFERENCE_LABEL) {
                this.value.reference_label = null;
                this.value.with_icon = null;
            }

            if (! [
                this.TABLE_CELL_TYPES.REFERENCE_VALUE,
                this.TABLE_CELL_TYPES.REFERENCE_VALUE_WITH_LINK,
            ].includes(this.value.type_id)) {
                this.value.reference_value = null;
            }

            if (! [
                this.TABLE_CELL_TYPES.TEXT,
                this.TABLE_CELL_TYPES.TEXT_WITH_ICON,
            ].includes(this.value.type_id)) {
                this.value.text = null;
                this.value.bold = null;
            }

            if (this.value.type_id !== this.TABLE_CELL_TYPES.TEXT_WITH_ICON) {
                this.value.icon = null;
            }

            if(! [
                this.TABLE_CELL_TYPES.LINK,
                this.TABLE_CELL_TYPES.LOGO_WITH_LINK,
                this.TABLE_CELL_TYPES.REFERENCE_VALUE_WITH_LINK,
            ].includes(this.value.type_id)) {
                this.value.link = null;
                this.value.link_id = null;
            }

            if (this.value.type_id !== this.TABLE_CELL_TYPES.LINK) {
                this.value.cta_style = null;
            }

            if (! [
                this.TABLE_CELL_TYPES.LOGO,
                this.TABLE_CELL_TYPES.LOGO_WITH_LINK,
            ].includes(this.value.type_id)) {
                this.value.logo = null;
            }
        },
    }
}
</script>
